/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui'
import { useApi } from '@/hooks'
import logger from '@/utils/logger'

import type { AdminUserDetail } from '../types'

const log = logger({ category: 'AdminUserDetailPage' })

interface Props {
  user: AdminUserDetail
  uuid: string
  isSelf: boolean
}

/** Removes the profile row entirely — for leftovers, not for revoking access. */
export const DeleteCard: React.FC<Props> = ({ user, uuid, isSelf }) => {
  const { api } = useApi()
  const navigate = useNavigate()
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete the Insights profile for ${user.email || user.uid}? ` +
          'This removes the row entirely, including their dashboards and settings. ' +
          'The Auth0 and Fresh accounts will not be affected — the user is only removed from Insights.'
      )
    ) {
      return
    }
    setIsDeleting(true)
    setDeleteError('')
    try {
      const response = await api.remove<{ uid: string }>({
        path: `/app/admin/users/${encodeURIComponent(uuid)}`,
        origin: 'AdminUserDetailPage#delete',
      })
      if (response.error) {
        setDeleteError(response.message || 'Could not delete')
        return
      }
      navigate('/admin/users')
    } catch (ex) {
      log.error(ex)
      setDeleteError('Could not delete')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <section className="card border border-error/30 bg-base-100 shadow">
      <div className="card-body">
        <h2 className="card-title text-base">Delete profile</h2>
        <p className="text-sm text-base-content/70">
          Removes this Insights profile row entirely, including dashboards and settings — for cleaning up leftovers. To
          revoke a real user&rsquo;s access, untick &ldquo;Access enabled&rdquo; instead.
        </p>

        {deleteError && <Alert message={deleteError} type="error" />}

        <div className="card-actions mt-2 justify-end">
          <button
            type="button"
            className="btn btn-outline btn-error btn-sm"
            disabled={isDeleting || isSelf}
            onClick={() => void handleDelete()}
          >
            {isDeleting ? 'Deleting…' : 'Delete profile'}
          </button>
        </div>
      </div>
    </section>
  )
}

export default DeleteCard
